import { useCallback, useMemo } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { HugeiconsIcon } from '@hugeicons/react'
import { PlusSignIcon } from '@hugeicons/core-solid-rounded'
import { Trans, useLingui } from '@lingui/react/macro'

import { GoodieFile, isNewFile } from '../types'
import FileUpload from './FileUpload'
import { GalleryMoveButtons, Root } from './GalleryDragDrop'
import { Preview } from './FilePreview'
import { PRESETS } from '../utils/resize'

interface Props {
  files: GoodieFile[]
  locale: GraphQL.AppLocale
  isDefaultLocale: boolean
  quickFiles?: GraphQL.FileFragmentFragment[]
  readOnly?: boolean
  name?: string
  invalid?: boolean
  accept?: HTMLInputElement['accept']
  onChange: (files: GoodieFile[]) => void
}

export default function GalleryUpload({
  files,
  quickFiles = [],
  locale,
  isDefaultLocale,
  onChange,
  readOnly = false,
  invalid = false,
  name,
  accept
}: Props) {
  const { t } = useLingui()

  const onAddFile = useCallback(
    (file: GoodieFile) => {
      onChange([file, ...files])
    },
    [files, onChange]
  )

  const onUpdateFile = useCallback(
    (index: number, file: GoodieFile) => {
      const newFiles = files.map(function (f, i) {
        if (i === index) return file
        return f
      })

      onChange(newFiles)
    },
    [files, onChange]
  )

  const onRemoveFile = useCallback(
    (index: number) => {
      const changedFiles = files.toSpliced(index, 1)

      onChange(changedFiles)
    },
    [files, onChange]
  )

  const onMoveFile = useCallback(
    (currentIndex: number, newIndex: number) => {
      const newFiles = [...files]
      const element = newFiles[currentIndex]
      newFiles.splice(currentIndex, 1)
      newFiles.splice(newIndex, 0, element)

      onChange(newFiles)

      // When we have a lot of files we should scroll them into view when moving them
      queueMicrotask(() => {
        const activeElement = document.activeElement
        const element = activeElement?.closest('.file-to-upload')

        element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
    },
    [files, onChange]
  )

  const suggestedFiles = useMemo(() => {
    return quickFiles.filter(quickFile => {
      return !files.some(file => !isNewFile(file) && file.id === quickFile.id)
    })
  }, [quickFiles, files])

  return (
    <div className="min-w-0 flex-1 flex flex-col">
      <div className="flex flex-row items-start overflow-x-auto overflow-y-hidden whitespace-nowrap px-1 py-2.5">
        {!readOnly && (
          <div className="px-1.5">
            <FileUpload
              locale={locale}
              isDefaultLocale={isDefaultLocale}
              value={null}
              onChange={onAddFile}
              accept={accept}
              crop={{
                aspectRatio: 2 / 3,
                shape: 'rect',
                allowPadding: false
              }}
              imageResize={PRESETS.DEFAULT}
              aria-label={t`Add new file`}
              className="shrink-0"
            />
          </div>
        )}
        {files.map(function (file, index) {
          const id = isNewFile(file) ? file.file.lastModified : file.id

          return (
            <Root
              key={id}
              currentPosition={index}
              file={file}
              maxPosition={files.length - 1}
              onChangePosition={newIndex => onMoveFile(index, newIndex)}
            >
              <div className="file-to-upload flex flex-col gap-1">
                <FileUpload
                  locale={locale}
                  isDefaultLocale={isDefaultLocale}
                  value={file}
                  accept={accept}
                  readOnly={readOnly}
                  onChange={file => onUpdateFile(index, file)}
                  onRemove={() => onRemoveFile(index)}
                  canReplace={false}
                  name={name}
                  aria-invalid={invalid}
                  crop={{
                    aspectRatio: 2 / 3,
                    shape: 'rect',
                    allowPadding: false
                  }}
                  imageResize={PRESETS.DEFAULT}
                  className="shrink-0"
                />
                <GalleryMoveButtons />
              </div>
            </Root>
          )
        })}
      </div>
      {suggestedFiles.length > 0 && (
        <SuggestedFiles
          files={suggestedFiles}
          onClick={onAddFile}
          locale={locale}
        />
      )}
    </div>
  )
}

function SuggestedFiles({
  files,
  locale,
  onClick
}: {
  files: GraphQL.FileFragmentFragment[]
  locale: GraphQL.AppLocale
  onClick: (file: GraphQL.FileFragmentFragment) => void
}) {
  const { t } = useLingui()

  return (
    <div className="flex flex-col gap-1 px-2.5">
      <h3 className="text-text text-heading3">
        <Trans>Suggested Files</Trans>
      </h3>
      <div className="flex flex-row w-full">
        <div className="flex flex-row items-start overflow-x-auto overflow-y-hidden whitespace-nowrap gap-1">
          {files.map(function (file) {
            const fileName = file.name

            return (
              <button
                className="relative h-24 aspect-2/3 rounded-small overflow-hidden"
                key={file.id}
                onClick={() => onClick(file)}
                aria-label={t`Choose file with name "${fileName}"`}
              >
                <Preview
                  locale={locale}
                  value={file}
                  readOnly
                  allowCaptions={false}
                  onEdit={() => {}}
                  onChangeCaptions={() => {}}
                  onChangeAltText={() => {}}
                  objectFit="cover"
                  hideControls
                />
                <div className="absolute size-full flex flex-row justify-center items-center inset-0 bg-black/20">
                  <HugeiconsIcon
                    icon={PlusSignIcon}
                    className="size-5 text-white"
                  />
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
